import * as React from 'react';
import * as _ from 'underscore';

import { IGlob } from '../../ts/Glob';
import { ngettext } from '../../../../_common/platform/ts/ngettext';
import DirsResourcesFlat from '../DirsResourcesFlat';
import { IMarkedItems } from '../../ts/Interfaces';
import { ISelectedItem } from '../DirsTreeFlat';

export interface IResourcesExportValue {
    selectedDir?: number;
    dirs?: string[];
    items?: string[];
}

interface IStateValue {
    selectedDir: number;
    markedDirs: IMarkedItems;
    markedItems: IMarkedItems;
}

function createStateValue(value: IResourcesExportValue, selectedItems: ISelectedItem[]) {
    let result: IStateValue;

    if (value != null) {
        const markedDirs: IMarkedItems = {};
        const markedItems: IMarkedItems = {};

        if (selectedItems != null) {
            selectedItems.forEach((item) => {
                if (item.type === 0) {
                    markedDirs[item.id] = item.title;
                } else {
                    markedItems[item.id] = item.title;
                }
            });
        }

        result = {
            selectedDir: value.selectedDir ?? -1,
            markedDirs: markedDirs,
            markedItems: markedItems
        };
    } else {
        result = {
            selectedDir: -1,
            markedDirs: {},
            markedItems: {}
        };
    }

    return result;
}

function getResultValue(value: IStateValue) {
    const newValue: IResourcesExportValue = {
        dirs: _.keys(value.markedDirs),
        items: _.keys(value.markedItems)
    };

    if (value.selectedDir != null) {
        newValue.selectedDir = value.selectedDir;
    }

    const selectedItems: ISelectedItem[] = [];

    _.each(value.markedDirs, (title, id) => {
        selectedItems.push({ type: 0, id: id, title: title });
    });
    _.each(value.markedItems, (title, id) => {
        selectedItems.push({ type: 1, id: id, title: title });
    });

    return { newValue, selectedItems };
}

function getSelectedMessage(value: IStateValue) {
    const markedDirs = Object.values(value.markedDirs);
    const dirsNumber = markedDirs.length;
    const markedItems = Object.values(value.markedItems);
    const workspacesNumber = markedItems.length;

    if (dirsNumber === 0 && workspacesNumber === 0) {
        return [<span key={'ask-ws-span'}>{'Select folders and resources to export.'}</span>];
    }

    const result = _.compact([
        dirsNumber > 0 ? (
            <div key={'dirs-div-ws'} className="selected-message-block">
                <strong>{markedDirs.join(', ')}</strong>
                <span>{ngettext('folder', 'folders', dirsNumber)}</span>
            </div>
        ) : null,

        workspacesNumber > 0 ? (
            <div key={'item-div-ws'} className="selected-message-block">
                <strong>{markedItems.join(', ')}</strong>
                <span>{ngettext('resource', 'resources', workspacesNumber)}</span>
            </div>
        ) : null
    ]);

    if (result.length === 2) {
        result.splice(1, 0, <span key={'and-span-ws'}>{'and'}</span>);
    }

    result.unshift(<span key={'you-span-ws'}>{'You have selected'}</span>);
    return result;
}

export default function ResourcesExport(props: {
    value: IResourcesExportValue;
    selectedItems: ISelectedItem[];
    glob: IGlob;

    onChange(value: IResourcesExportValue, selectedItems: any[]): void;
}) {
    const [value, setValue] = React.useState<IStateValue>(createStateValue(props.value, props.selectedItems));

    const processOnChange = React.useCallback(
        (value: IStateValue) => {
            if (props.onChange != null) {
                const { newValue, selectedItems } = getResultValue(value);

                props.onChange(newValue, selectedItems);
            }

            setValue(value);
        },
        [props.onChange]
    );

    const handleOnDirSelect = (id: number) => {
        const newValue = { ...value };

        newValue.selectedDir = id;
        processOnChange(newValue);
    };

    const handleOnDirsMark = (marked: IMarkedItems) => {
        const newValue = { ...value };

        newValue.markedDirs = marked;
        processOnChange(newValue);
    };

    const handleOnItemsMark = (marked: IMarkedItems) => {
        const newValue = { ...value };

        newValue.markedItems = marked;
        processOnChange(newValue);
    };

    const handleOnMark = (markedDirs: IMarkedItems, markedItems: IMarkedItems) => {
        const newValue = { ...value };

        newValue.markedDirs = markedDirs;
        newValue.markedItems = markedItems;
        processOnChange(newValue);
    };

    return (
        <div className="workspaces-export">
            <div className="selected-message">{getSelectedMessage(value)}</div>

            <DirsResourcesFlat
                instanceId={'resources-export'}
                name={'Resources Root'}
                selectedDirId={value.selectedDir}
                markMode={true}
                markedDirs={value.markedDirs}
                markedItems={value.markedItems}
                glob={props.glob}
                onDirSelect={handleOnDirSelect}
                onDirsMark={handleOnDirsMark}
                onItemsMark={handleOnItemsMark}
                onMark={handleOnMark}
            />
        </div>
    );
}
